/**
 * Admin Scope Registrations
 * --------------------------
 * Reusable registration + QR + check-in for ANY scope on the ecosystem
 * (event, school, church, ministry, campaign, org). Admins can create an
 * attendee list on the fly, or drop a public registration link.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/store';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  QrCode, Users, CheckCircle2, Plus, Copy, ScanLine, Loader2, Clipboard, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SCOPES = ['event', 'school', 'church', 'ministry', 'campaign', 'org'];
const CATEGORIES = ['attendee', 'visitor', 'volunteer', 'speaker', 'guest', 'student'];

function KpiCard({ title, value, icon: Icon, tone = 'indigo' }) {
  const t = {
    indigo: 'text-indigo-600 bg-indigo-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
  }[tone];
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
          </div>
          <div className={`p-2 rounded-lg ${t}`}><Icon className="h-5 w-5" /></div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminScopeRegistrations() {
  const { token } = useAuthStore();
  const hdrs = () => ({ Authorization: `Bearer ${token || getToken()}`, 'Content-Type': 'application/json' });
  const qc = useQueryClient();

  const [scopeType, setScopeType] = useState('school');
  const [scopeId, setScopeId] = useState('');
  const [showRegDialog, setShowRegDialog] = useState(false);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [qrInput, setQrInput] = useState('');
  const [formData, setFormData] = useState({
    scope_type: 'school', scope_id: '', scope_label: '',
    full_name: '', email: '', phone: '', category: 'attendee',
  });

  const enabled = !!scopeId;

  const { data: regs = { items: [] }, isLoading } = useQuery({
    queryKey: ['scope-reg-list', scopeType, scopeId],
    enabled,
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/scope-reg/by-scope?scope_type=${scopeType}&scope_id=${scopeId}&limit=300`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  const { data: stats } = useQuery({
    queryKey: ['scope-reg-stats', scopeType, scopeId],
    enabled,
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/scope-reg/stats?scope_type=${scopeType}&scope_id=${scopeId}`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  const { data: totals } = useQuery({
    queryKey: ['scope-reg-totals'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/scope-reg/totals`, { headers: hdrs() });
      if (!r.ok) return { by_scope_type: {}, total: 0 };
      return r.json();
    },
  });

  const createReg = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_URL}/api/scope-reg/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, scope_type: scopeType, scope_id: scopeId }),
      });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
    onSuccess: (d) => {
      toast.success(`Registered · QR: ${d.qr_code}`);
      qc.invalidateQueries({ queryKey: ['scope-reg-list'] });
      qc.invalidateQueries({ queryKey: ['scope-reg-stats'] });
      setShowRegDialog(false);
      setFormData({ ...formData, full_name: '', email: '', phone: '' });
    },
    onError: (e) => toast.error(e.message),
  });

  const checkIn = useMutation({
    mutationFn: async (qr) => {
      const r = await fetch(`${API_URL}/api/scope-reg/check-in`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ qr_code: qr, scope_type: scopeType, scope_id: scopeId }),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.detail || 'Check-in failed');
      }
      return r.json();
    },
    onSuccess: (d) => {
      toast.success(`Checked in: ${d.full_name}${d.already_checked_in ? ' (already checked in)' : ''}`);
      qc.invalidateQueries({ queryKey: ['scope-reg-list'] });
      qc.invalidateQueries({ queryKey: ['scope-reg-stats'] });
      setQrInput('');
    },
    onError: (e) => toast.error(e.message),
  });

  const publicLink = enabled ? `${window.location.origin}/register?scope_type=${scopeType}&scope_id=${scopeId}` : '';

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="scope-registrations-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <span className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 text-white">
                <QrCode className="h-6 w-6" />
              </span>
              Scope Registrations
            </h1>
            <p className="text-slate-500 mt-1">One registration + QR + check-in flow, reusable by every scope.</p>
          </div>
        </div>

        {/* Ecosystem-wide totals */}
        {totals?.by_scope_type && Object.keys(totals.by_scope_type).length > 0 && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Ecosystem totals</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(totals.by_scope_type).map(([k, v]) => (
                  <Badge key={k} variant="outline" className="text-xs px-3 py-1">
                    <span className="capitalize font-semibold">{k}</span>
                    <span className="ml-2 text-slate-600">{v.total} reg</span>
                    <span className="ml-2 text-emerald-600">✓ {v.checked_in}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scope selector */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Choose scope</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Scope type</Label>
              <Select value={scopeType} onValueChange={setScopeType}>
                <SelectTrigger data-testid="scope-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SCOPES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Scope ID (event/school/campaign id)</Label>
              <Input value={scopeId} onChange={(e) => setScopeId(e.target.value.trim())} placeholder="paste the scope's id" data-testid="scope-id-input" />
            </div>
          </CardContent>
        </Card>

        {enabled && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <KpiCard title="Total" value={stats?.total ?? 0} icon={Users} />
              <KpiCard title="Checked-in" value={stats?.checked_in ?? 0} icon={CheckCircle2} tone="emerald" />
              <KpiCard title="Registered" value={stats?.registered ?? 0} icon={QrCode} tone="amber" />
              <KpiCard title="Check-in rate" value={`${stats?.check_in_rate ?? 0}%`} icon={CheckCircle2} tone="emerald" />
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 items-center">
              <Button onClick={() => setShowRegDialog(true)} data-testid="open-register-dialog">
                <Plus className="h-4 w-4 mr-1" /> Register someone
              </Button>
              <Button variant="outline" onClick={() => setShowScanDialog(true)} data-testid="open-scan-dialog">
                <ScanLine className="h-4 w-4 mr-1" /> Check-in (QR)
              </Button>
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(publicLink); toast.success('Public registration link copied'); }} data-testid="copy-public-link">
                <Copy className="h-4 w-4 mr-1" /> Copy public link
              </Button>
            </div>

            {/* Table */}
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Registrations</CardTitle></CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-32" /> : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>QR</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {regs.items.length === 0 ? (
                        <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No registrations yet.</TableCell></TableRow>
                      ) : regs.items.map((r) => (
                        <TableRow key={r._id} data-testid={`reg-${r._id}`}>
                          <TableCell className="font-medium">{r.full_name}</TableCell>
                          <TableCell className="text-sm text-slate-600">{r.email || '—'}</TableCell>
                          <TableCell className="text-xs capitalize">{r.category}</TableCell>
                          <TableCell>
                            <Badge className={
                              r.status === 'checked_in' ? 'bg-emerald-500' :
                              r.status === 'cancelled' ? 'bg-slate-400' : 'bg-indigo-500'
                            }>{r.status.replace('_', ' ')}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            <button onClick={() => { navigator.clipboard.writeText(r.qr_code); toast.success('QR copied'); }} className="hover:underline" data-testid={`copy-qr-${r._id}`}>
                              {r.qr_code}
                            </button>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Register dialog */}
        <Dialog open={showRegDialog} onOpenChange={setShowRegDialog}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="register-dialog">
            <DialogHeader>
              <DialogTitle>Register for {scopeType}</DialogTitle>
              <DialogDescription>Adds to this scope with a unique QR. If the email is already linked to a person in the ecosystem, they'll be merged.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Full name</Label><Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} data-testid="reg-full-name" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="reg-email" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Label (optional)</Label><Input value={formData.scope_label} onChange={(e) => setFormData({ ...formData, scope_label: e.target.value })} placeholder="e.g. 'FWBC Open Day 2026'" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRegDialog(false)}>Cancel</Button>
              <Button disabled={!formData.full_name || createReg.isPending} onClick={() => createReg.mutate()} data-testid="reg-submit">
                {createReg.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Register'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Scan/check-in dialog */}
        <Dialog open={showScanDialog} onOpenChange={setShowScanDialog}>
          <DialogContent className="max-w-sm" data-testid="scan-dialog">
            <DialogHeader>
              <DialogTitle>Check in by QR</DialogTitle>
              <DialogDescription>Paste or type the QR code. Only QRs for this scope will check in.</DialogDescription>
            </DialogHeader>
            <Input value={qrInput} onChange={(e) => setQrInput(e.target.value.trim())} placeholder="QR code…" autoFocus data-testid="qr-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowScanDialog(false)}>Cancel</Button>
              <Button disabled={!qrInput || checkIn.isPending} onClick={() => checkIn.mutate(qrInput)} data-testid="qr-checkin-submit">
                {checkIn.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check in'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
